import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useChat } from '@/hooks/use-chat';
import { cn } from '@/lib/utils';
import { Eraser, Sparkles, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatInput } from './ChatInput';
import { ChatMessageList } from './ChatMessageList';
const STORAGE_KEY = 'newlumen_chat_panel_width';
const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 350;
const MAX_WIDTH = 600;
function loadWidth() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const n = parseInt(stored, 10);
            if (n >= MIN_WIDTH && n <= MAX_WIDTH)
                return n;
        }
    }
    catch {
        /* ignore */
    }
    return DEFAULT_WIDTH;
}
export function ChatPanel({ onClose }) {
    const { t } = useTranslation();
    const { messages, isStreaming, sendMessage, cancel, clearSession } = useChat();
    const [width, setWidth] = useState(loadWidth);
    const dragging = useRef(false);
    const startX = useRef(0);
    const startW = useRef(0);
    // Mirrors the width state for event handlers (persisting on pointerup,
    // seeding the next drag) without re-creating them per resize frame. Only
    // written from handlers — never during render.
    const latestWidth = useRef(width);
    // Pointer Events (not mouse-only) so touch/pen can resize too; pointer capture
    // keeps the drag alive even when the cursor leaves the 12px hit strip.
    const handlePointerDown = useCallback((e) => {
        e.preventDefault();
        dragging.current = true;
        startX.current = e.clientX;
        startW.current = latestWidth.current;
        e.currentTarget.setPointerCapture(e.pointerId);
    }, []);
    const handlePointerMove = useCallback((e) => {
        if (!dragging.current)
            return;
        const delta = startX.current - e.clientX;
        const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW.current + delta));
        latestWidth.current = next;
        setWidth(next);
    }, []);
    const endDrag = useCallback((e) => {
        if (!dragging.current)
            return;
        dragging.current = false;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        try {
            localStorage.setItem(STORAGE_KEY, String(latestWidth.current));
        }
        catch {
            /* ignore */
        }
    }, []);
    const handleSend = useCallback((text) => sendMessage(text), [sendMessage]);
    return (_jsxs("div", { 
        // The panel is pinned to the right screen edge: pad for the iPhone
        // landscape inset (env() is 0 elsewhere).
        className: "border-border bg-panel-chrome flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l pr-[env(safe-area-inset-right)]", style: { width: `min(${width}px, 100vw)` }, children: [_jsx("div", { role: "separator", "aria-orientation": "vertical", "aria-label": t('chat.resizeHandle'), onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, className: "hover:bg-accent-ai/40 absolute top-0 bottom-0 left-0 z-10 w-[3px] cursor-col-resize touch-none transition-colors before:absolute before:inset-y-0 before:left-0 before:w-3 before:content-[''] pointer-coarse:before:w-6" }), _jsxs("div", { className: "border-border flex h-11 shrink-0 items-center justify-between border-b px-3", children: [_jsxs("div", { className: "text-foreground flex items-center gap-2 text-sm font-medium", children: [_jsx(Sparkles, { className: "text-accent-ai h-3.5 w-3.5" }), t('chat.aiResident')] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("button", { type: "button", onClick: clearSession, className: cn('flex h-9 w-9 items-center justify-center rounded-md transition-colors', 'text-muted-foreground hover:bg-muted hover:text-foreground'), title: t('chat.newChat'), "aria-label": t('chat.newChat'), children: _jsx(Eraser, { className: "h-3.5 w-3.5" }) }), _jsx("button", { type: "button", onClick: onClose, className: cn('flex h-9 w-9 items-center justify-center rounded-md transition-colors', 'text-muted-foreground hover:bg-muted hover:text-foreground'), title: t('chat.close'), "aria-label": t('chat.close'), children: _jsx(X, { className: "h-3.5 w-3.5" }) })] })] }), _jsx(ChatMessageList, { messages: messages, className: "min-h-0 min-w-0 flex-1" }), _jsx(ChatInput, { onSend: handleSend, onCancel: cancel, isStreaming: isStreaming })] }));
}
